/**
 * The waiting-hail prompt on the flight HUD (M7 plan S31): one line, "<name> is hailing · [O] Answer", for the
 * oldest waiting hail. It never times out; it goes when the hail is answered.
 *
 * H11 (D-617 §6): the prompt is a pinned chip of the top-centre status stack (`hud/status.ts`), so it sits with the
 * jurisdiction chip and the toasts instead of floating mid-right, and newer toasts never push it out.
 *
 * The M1 HUD rule: the DOM is written only when the shown hail changes. `writeHailPrompt` runs every frame and
 * allocates nothing unless it writes (an integer and a reference compare).
 */
import { hudStatus, statusHide, statusShow, STATUS_HIDDEN, type StatusStack } from '../../hud/status'
import { waitingHail, hailPromptText, type CommsLabels, type CommsSession, type Hail, type SpeakerLookup } from './model'

/** The prompt element's fields the view writes (an `HTMLElement` in the game, a plain object in tests). */
export interface PromptElement {
  textContent: string | null
  readonly dataset: Record<string, string | undefined>
  readonly style: { transform: string }
}

export interface HailPromptView {
  readonly el: PromptElement
  /** The status stack the prompt shows in. */
  readonly stack: StatusStack
  /** The hail the DOM shows (diffed). */
  shown: Hail | null
  /** DOM writes so far (tests). */
  writes: number
}

export function createHailPromptView(el: PromptElement, stack: StatusStack = hudStatus): HailPromptView {
  el.dataset.on = '0'
  el.textContent = ''
  el.style.transform = STATUS_HIDDEN
  return { el, stack, shown: null, writes: 0 }
}

/** Creates the prompt's element under the HUD root, as a status-stack chip. */
export function mountHailPrompt(parent: HTMLElement, stack: StatusStack = hudStatus): HailPromptView & { readonly el: HTMLElement } {
  const el = parent.ownerDocument.createElement('div')
  el.className = 'hud-chip cm-hail-chip'
  el.setAttribute('data-testid', 'comms-hail')
  el.setAttribute('role', 'status')
  parent.appendChild(el)
  const v = createHailPromptView(el, stack)
  return { ...v, el }
}

/** Writes the prompt only when the waiting hail changed. */
export function writeHailPrompt(v: HailPromptView, s: Readonly<CommsSession>, speakers: SpeakerLookup, labels: CommsLabels, keys: readonly string[]): void {
  const h = waitingHail(s)
  if (h === v.shown) return
  v.shown = h
  v.writes++
  if (h === null) {
    statusHide(v.stack, v.el)
    v.el.dataset.on = '0'
    v.el.textContent = ''
    return
  }
  v.el.dataset.kind = h.kind
  v.el.textContent = hailPromptText(h, speakers, labels, keys)
  statusShow(v.stack, v.el, true)
}
